import { StandardResolutionReasons, type Client } from '@openfeature/web-sdk'
import { useRoute, useState } from '#imports'
import { coerceBoolean, parseQueryValue } from '../../openfeature/queryCoercion'
import type { ServerFlagState } from '../../openfeature/serverFlagClient'
import { US_DEFAULT_ALLOW_COOKIE } from '../../openfeature/utils'
import { getCookieClient } from '../storage/utils'

export const US_DEFAULT_ALLOW_FLAG = 'enableUsDefaultAllow'

/**
 * Whether this page load starts consent with user-identity's US default.
 *
 * The consent machine starts before OpenFeature loads and keeps the options it
 * started with, so the flag is read from what the page already has: a query
 * override, the server's evaluation on a rendered home page, then the value the
 * browser provider resolved on an earlier load. A first visit gets `false`.
 */
export function usDefaultAllowAtBoot(): boolean {
  const override = useRoute().query[US_DEFAULT_ALLOW_FLAG]
  if (typeof override === 'string') {
    return coerceBoolean(parseQueryValue(override), false).value
  }

  const server = useState<ServerFlagState | null>('ssr-flags').value
  const rendered = server?.degraded
    ? undefined
    : server?.values[US_DEFAULT_ALLOW_FLAG]?.value
  if (typeof rendered === 'boolean') {
    return rendered
  }

  return getCookieClient(US_DEFAULT_ALLOW_COOKIE) === 'true'
}

/**
 * Keeps what the browser provider resolved for the next load's boot read. A
 * failed evaluation leaves the last value alone, so a ConfigCat outage does not
 * move visitors between arms.
 */
export function rememberUsDefaultAllow(
  client: Pick<Client, 'getBooleanDetails'>,
  cookie: { value: string | null | undefined }
): void {
  const details = client.getBooleanDetails(US_DEFAULT_ALLOW_FLAG, false)
  if (details.reason === StandardResolutionReasons.ERROR) return
  cookie.value = String(details.value)
}
